<script setup lang="ts">
import { onMounted, ref, watch, type Ref } from 'vue'
import { Gesuch, GesuchCollection, GesuchStatus } from '@/models/Gesuch'
import { useAuthUser, useGlobals } from '@/helpers/globals'
import { TgWappenSvg, ShWappenSvg } from '@/helpers/images'
import { useRouter } from 'vue-router'
import type { DataTableHeader } from 'vuetify'
import StatusLabel from '@/components/gesuch-status-label.vue'
import KpLink from '@/components/link.vue'
import SearchField from '@/components/SearchField.vue'
import { useBrowserSession } from '@/helpers/browser_session'
import ConfirmDlg from '@/components/confirm-dlg.vue'
import { errToStr } from '@/helpers/tools'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/toast'
import { instance as getApi } from '@/helpers/api.js'

const props = defineProps({
    hideDefaultHeader: {
        type: Boolean,
        default: false,
    },
    hideDefaultFooter: {
        type: Boolean,
        default: false,
    },
    paging: {
        type: Boolean,
        default: true,
    },
    status: {
        type: [String, Array],
        default: 'all',
    },
})

const { t } = useI18n()
const user = useAuthUser()
const gueltigkeitZusageDeadline = useGlobals().config?.gueltigkeit_zusage_deadline || 0
const searchString = ref('')
const gesuchLoading = ref(true)
const confirmDelDlg = ref(false)
const extendCommitmentDlg = ref(false)
const extendRequestedDlg = ref(false)
const extendCommitmentItem: Ref<Gesuch | null> = ref(null)
const toDelete: Ref<Gesuch | null> = ref(null)

const headers: Ref<DataTableHeader[]> = ref([
    { key: 'kanton', width: '80px', align: 'center' },
    {
        title: 'Gültigkeit Zusage',
        key: 'gueltigkeit_zusage_in_tagen',
        width: '70px',
        align: 'center' as const,
        sortable: true,
    },
    { title: 'Info', key: 'nummer', width: '300px', align: 'start' },
    {
        title: 'Eigentümer',
        key: 'eigentuemer_display_name',
        sortable: true,
        align: 'start',
    },
    {
        title: 'Unternehmer',
        key: 'unternehmer_display_name',
        sortable: true,
        align: 'start',
    },
    { title: 'Daten', key: 'created', sortable: true, align: 'start' },
    { value: 'actions', minWidth: '110px', width: '120px', sortable: false, align: 'end' },
])
const gesuche = new GesuchCollection()
const visibleHeaders: Ref<string[]> = ref([])
const total = ref(0)
const headerList = ref([
    { id: 0, value: 'status', text: 'Status' },
    { id: 1, value: 'eigentuemer_display_name', text: 'Eigentümer' },
    { id: 2, value: 'unternehmer_display_name', text: 'Unternehmer' },
    { id: 3, value: 'bevollmaechtigter_display_name', text: 'Bevollmächtigter' },
    { id: 4, value: 'anzahl_liegenschaften', text: 'Liegenschaft' },
    { id: 5, value: 'created', text: 'Daten' },
])

watch(visibleHeaders, (values: string[]) => {
    const newHeader: DataTableHeader[] = [
        { value: 'kanton', width: '80px', align: 'center' },
        {
            title: 'Gültigkeit Zusage',
            value: 'gueltigkeit_zusage_in_tagen',
            width: '70px',
            align: 'center',
            sortable: true,
        },
        { title: 'Gesuch', value: 'nummer', align: 'start', width: '300px' },
    ]
    if (values.includes('status')) {
        newHeader.push({ title: 'Status', value: 'status', align: 'start' })
    }
    if (values.includes('eigentuemer_display_name')) {
        newHeader.push({
            title: 'Eigentümer',
            value: 'eigentuemer_display_name',
            align: 'start',
            sortable: true,
        })
    }
    if (values.includes('unternehmer_display_name')) {
        newHeader.push({
            title: 'Unternehmer',
            value: 'unternehmer_display_name',
            align: 'start',
            sortable: true,
        })
    }
    if (values.includes('bevollmaechtigter_display_name')) {
        newHeader.push({
            title: 'Bevollmächtigter',
            value: 'bevollmaechtigter_display_name',
            align: 'start',
            sortable: true,
        })
    }
    if (values.includes('anzahl_liegenschaften')) {
        newHeader.push({
            title: 'Liegenschaft',
            value: 'anzahl_liegenschaften',
            align: 'start',
            sortable: false,
        })
    }
    if (values.includes('created')) {
        newHeader.push({
            title: 'Daten',
            value: 'created',
            align: 'start',
            sortable: true,
        })
    }
    newHeader.push({
        value: 'actions',
        align: 'start',
        minWidth: '110px',
        width: '120px',
        sortable: false,
    })
    useBrowserSession('gesuchelist').setSessionValue(
        'visibleheaders',
        newHeader.map((header) => header.value),
    )
    headers.value = newHeader
})
// },
onMounted(() => {
    visibleHeaders.value = useBrowserSession('gesuchelist').getSessionValue('visibleheaders', [
        'eigentuemer_display_name',
        'unternehmer_display_name',
        'anzahl_liegenschaften',
        'created',
    ])
})

async function getGesuche({
    page,
    itemsPerPage,
    sortBy,
}: {
    page: number
    itemsPerPage: number
    sortBy: string[]
}) {
    gesuchLoading.value = true
    try {
        gesuche.setQueryParam('status', props.status)
        gesuche.setQueryParam('itemsPerPage', itemsPerPage)
        gesuche.setQueryParam('page', page)
        gesuche.setQueryParam('sortBy', sortBy)
        gesuche.setQueryParam('query', searchString.value)
        await gesuche.query()
        total.value = gesuche.getServerTotal()
    } catch (e) {
        console.log(e)
    } finally {
        gesuchLoading.value = false
    }
}

function executeSearch(value: string) {
    searchString.value = value
}

function allowDestroy(gesuch: Gesuch) {
    if (
        (gesuch.status !== GesuchStatus.READY_FOR_SUBMIT && gesuch.status !== GesuchStatus.NEW) ||
        gesuch.isLocked(user) ||
        gesuch.isReadOnly()
    ) {
        return false
    }
    return gesuch.ersteller_id === user?.id
}

function destroyItem(item: Gesuch) {
    toDelete.value = item
    confirmDelDlg.value = true
}
async function confirmDestroy() {
    if (toDelete.value) {
        try {
            await toDelete.value.destroy()
            gesuche.remove(toDelete.value)
            toDelete.value = null
            total.value = total.value - 1
            useToast().add({ text: t('delete_gesuch_success'), color: 'success' })
        } catch (e) {
            useToast().add({ text: errToStr(e), color: 'error' })
        }
    }
}
const router = useRouter()
function rowclicked(item: MouseEvent) {
    const row = item.currentTarget as HTMLTableRowElement
    const ges = gesuche.at((row.rowIndex || -2) - 1)
    router.push({ name: 'gesuch', params: { nr: ges?.nummer } })
}

function extendCommitment(item: Gesuch) {
    extendCommitmentItem.value = item
    extendCommitmentDlg.value = true
}
async function confirmExtendCommitment() {
    if (extendCommitmentItem.value) {
        try {
            await getApi().post('/gesuch/extensionRequest', {
                gesuch_id: extendCommitmentItem.value.id,
            })
            extendRequestedDlg.value = true
        } catch (e) {
            useToast().add({ text: errToStr(e), color: 'error' })
        }
    }
}
</script>

<template>
    <div class="kp-large-container">
        <v-data-table-server
            class="gesuche-liste"
            :hide-default-header="hideDefaultHeader"
            :hide-default-footer="hideDefaultFooter"
            :headers="headers"
            :items="gesuche.getModels()"
            :items-length="total"
            :items-per-page="10"
            :search="searchString"
            item-key="id"
            :loading="gesuchLoading"
            :no-data-text="$t('no_records')"
            :items-per-page-text="$t('items_per_page')"
            :footer-props="{
                itemsPerPageOptions: [10, 25, 50, 100, -1],
            }"
            @click:row="rowclicked"
            @update:options="getGesuche"
        >
            <template v-slot:top>
                <div class="d-flex align-end justify-end">
                    <SearchField
                        class="white--text"
                        @executeSearch="executeSearch"
                        max-width="500px"
                        width="80vw"
                    ></SearchField>
                    <v-menu left :close-on-content-click="false">
                        <template v-slot:activator="{ props }">
                            <v-btn
                                icon="fas fa-cog"
                                color="primary"
                                variant="text"
                                v-bind="props"
                            ></v-btn>
                        </template>
                        <v-list v-model:selected="visibleHeaders" select-strategy="leaf">
                            <v-list-subheader>Spalten anzeigen</v-list-subheader>
                            <v-list-item
                                v-for="header in headerList"
                                :key="header.value"
                                :title="header.text"
                                :value="header.value"
                            >
                                <template v-slot:prepend="{ isSelected, select }">
                                    <v-list-item-action start>
                                        <v-checkbox-btn
                                            :model-value="isSelected"
                                            @update:model-value="select"
                                        ></v-checkbox-btn>
                                    </v-list-item-action>
                                </template>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </template>
            <template v-slot:[`item.kanton`]="{ item }">
                <div>
                    <img v-if="item.kanton === 'TG'" :src="TgWappenSvg" width="36px" class="ma-1" />
                    <img v-if="item.kanton === 'SH'" :src="ShWappenSvg" width="36px" class="ma-1" />
                </div>
            </template>
            <template v-slot:[`item.gueltigkeit_zusage_in_tagen`]="{ item }">
                <v-tooltip right>
                    <template v-slot:activator="{ props }">
                        <v-icon v-if="item.isClosed()" class="pt-1"> fas fa-minus </v-icon>
                        <v-icon
                            v-else-if="item.gueltigkeit_zusage_in_tagen === null"
                            class="pt-1"
                            v-bind="props"
                            icon="fas fa-minus"
                        >
                        </v-icon>
                        <v-icon
                            v-else-if="item.gueltigkeit_zusage_in_tagen < 0"
                            color="red"
                            class="pt-1"
                            v-bind="props"
                            icon="fas fa-exclamation-circle"
                        >
                        </v-icon>
                        <v-icon
                            v-else-if="
                                item.gueltigkeit_zusage_in_tagen <= gueltigkeitZusageDeadline
                            "
                            color="orange"
                            class="pt-1"
                            v-bind="props"
                            icon="fas fa-exclamation-circle"
                        >
                        </v-icon>
                        <v-icon
                            v-else-if="item.gueltigkeit_zusage_in_tagen > gueltigkeitZusageDeadline"
                            color="green"
                            class="pt-1"
                            v-bind="props"
                            icon="fas fa-circle"
                        >
                        </v-icon>
                    </template>
                    <span v-if="item.gueltigkeit_zusage_in_tagen === null">{{
                        $t('no_zusage')
                    }}</span>
                    <span v-else-if="item.gueltigkeit_zusage_in_tagen > 0">{{
                        $t('zusage_deadline_in_days', {
                            days: item.gueltigkeit_zusage_in_tagen,
                            date: item.gueltigkeit_zusage?.format('DD.MM.YYYY'),
                        })
                    }}</span>
                    <span v-else-if="item.gueltigkeit_zusage_in_tagen < 0">{{
                        $t('zusage_deadline_expired_since', {
                            days: Math.abs(item.gueltigkeit_zusage_in_tagen),
                            date: item.gueltigkeit_zusage?.format('DD.MM.YYYY'),
                        })
                    }}</span>
                    <span v-else-if="item.gueltigkeit_zusage_in_tagen === 0">{{
                        $t('zusage_deadline_today', {
                            date: item.gueltigkeit_zusage?.format('DD.MM.YYYY'),
                        })
                    }}</span>
                </v-tooltip>
            </template>
            <template v-slot:[`item.nummer`]="{ item }">
                <div>
                    <div class="gesuch-title">
                        <v-icon v-if="item.isLocked(user)" color="red" x-small> fas fa-lock</v-icon>
                        {{ item.nummer }}
                    </div>
                    <div v-if="!visibleHeaders.includes('status')" class="gesuch-status">
                        <StatusLabel :status="item.status" />
                    </div>
                    <div class="gesuch-subtitle">
                        {{ item.foerderkategorie_apollon }}
                    </div>
                    <div
                        v-if="
                            item.anzahl_liegenschaften > 0 &&
                            visibleHeaders.includes('anzahl_liegenschaften')
                        "
                        class="gesuch-anz-liegenschaften"
                    >
                        <v-icon size="x-small">far fa-house</v-icon>
                        {{ item.anzahl_liegenschaften }}
                    </div>
                </div>
            </template>
            <template v-slot:[`item.status`]="{ item }">
                <div>
                    <div class="gesuch-title">
                        <span class="gesuch-status">
                            <StatusLabel :status="item.status" />
                        </span>
                    </div>
                </div>
            </template>
            <template v-slot:[`item.eigentuemer_display_name`]="{ item }">
                <div>
                    <div>
                        <strong>{{ item.eigentuemer_display_name }}</strong>
                    </div>
                    <div>{{ item.eigentuemer_adresse }}</div>
                    <div>
                        {{
                            item.eigentuemer_laendercode && item.eigentuemer_laendercode !== 'CH'
                                ? item.eigentuemer_laendercode + '-'
                                : ''
                        }}{{ item.eigentuemer_plz }} {{ item.eigentuemer_ort }}
                    </div>
                    <div>
                        <kp-link mailto :href="item.eigentuemer_email" />
                    </div>
                </div>
            </template>
            <template v-slot:[`item.unternehmer_display_name`]="{ item }">
                <div>
                    <div>
                        <strong>{{ item.unternehmer_display_name }}</strong>
                    </div>
                    <div>{{ item.unternehmer_adresse }}</div>
                    <div>
                        {{
                            item.unternehmer_laendercode && item.unternehmer_laendercode !== 'CH'
                                ? item.unternehmer_laendercode + '-'
                                : ''
                        }}{{ item.unternehmer_plz }} {{ item.unternehmer_ort }}
                    </div>
                    <div>
                        <kp-link mailto :href="item.unternehmer_email" />
                    </div>
                </div>
            </template>
            <template v-slot:[`item.bevollmaechtigter_display_name`]="{ item }">
                <div>
                    <div>
                        <strong>{{ item.bevollmaechtigter_display_name }}</strong>
                    </div>
                    <div>{{ item.bevollmaechtigter_adresse }}</div>
                    <div>
                        {{
                            item.bevollmaechtigter_laendercode &&
                            item.bevollmaechtigter_laendercode !== 'CH'
                                ? item.bevollmaechtigter_laendercode + '-'
                                : ''
                        }}{{ item.bevollmaechtigter_plz }} {{ item.bevollmaechtigter_ort }}
                    </div>
                    <div>
                        <kp-link mailto :href="item.bevollmaechtigter_email" />
                    </div>
                </div>
            </template>
            <template v-slot:[`item.anzahl_liegenschaften`]="{ item }">
                <div>
                    <div>
                        {{
                            (item?.data?.liegenschaften?.length || 0) > 0
                                ? item.data?.liegenschaften[0].liegenschaft?.adresse
                                : ''
                        }}
                    </div>
                    <div>
                        {{
                            (item?.data?.liegenschaften?.length || 0) > 0
                                ? item.data?.liegenschaften[0].liegenschaft?.ort_plz
                                : ''
                        }}
                        {{
                            (item?.data?.liegenschaften?.length || 0) > 0
                                ? item.data?.liegenschaften[0].liegenschaft?.ort
                                : ''
                        }}
                    </div>
                    <div v-if="item?.data?.liegenschaften?.length == 2">
                        (+ {{ $t('one_other_object') }})
                    </div>
                    <div v-if="(item?.data?.liegenschaften?.length || 0) > 2">
                        (+{{
                            (item?.data?.liegenschaften?.length || 0) -
                            1 +
                            ' ' +
                            $t('other_objects')
                        }})
                    </div>
                </div>
            </template>
            <template v-slot:[`item.created`]="{ item }">
                <div>
                    <div v-if="item.zahlung_datum?.isValid()">
                        {{ $t('payment') }}: {{ item.zahlung_datum?.format('DD.MM.YYYY') }}
                    </div>
                    <div v-if="item.projektabschluss_erstmals_eingereicht_datum?.isValid()">
                        {{ $t('project_done') }}:
                        {{ item.projektabschluss_erstmals_eingereicht_datum?.format('DD.MM.YYYY') }}
                    </div>
                    <div v-if="item.foerderzusage_datum?.isValid()">
                        {{ $t('foerderzusage') }}:
                        {{ item.foerderzusage_datum?.format('DD.MM.YYYY') }}
                    </div>
                    <div v-if="item.gesuch_erstmals_eingereicht_datum?.isValid()">
                        {{ $t('submitted') }}:
                        {{ item.gesuch_erstmals_eingereicht_datum?.format('DD.MM.YYYY') }}
                    </div>
                    <div>{{ $t('created') }}: {{ item.created?.format('DD.MM.YYYY') }}</div>
                </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <span>
                    <v-btn
                        class="mr-1"
                        variant="text"
                        size="x-small"
                        color="accent"
                        icon="fas fa-pen"
                        @click.stop="$router.push({ name: 'gesuch', params: { nr: item.nummer } })"
                    >
                    </v-btn>

                    <v-tooltip v-if="!item.isClosed()" left>
                        <template v-slot:activator="{ props }">
                            <v-btn
                                v-if="item && item.gueltigkeit_zusage_in_tagen"
                                class="mr-1"
                                variant="text"
                                size="x-small"
                                icon="fas fa-calendar-plus"
                                color="orange"
                                v-bind="props"
                                @click.stop="extendCommitment(item)"
                            >
                            </v-btn>
                        </template>
                        <span>{{ $t('extend_commitment') }}</span>
                    </v-tooltip>

                    <v-tooltip v-if="!item.isClosed()" left>
                        <template v-slot:activator="{ props }">
                            <v-btn
                                v-if="allowDestroy(item)"
                                class="mr-1"
                                variant="text"
                                size="x-small"
                                icon="fas fa-trash"
                                color="error"
                                v-bind="props"
                                @click.stop="destroyItem(item)"
                            >
                            </v-btn>
                        </template>
                        <span>{{ $t('delete_gesuch') }}</span>
                    </v-tooltip>
                </span>
            </template>
        </v-data-table-server>
        <ConfirmDlg
            v-model:model-value="confirmDelDlg"
            :title="
                toDelete && toDelete.status === GesuchStatus.NEW
                    ? $t('bestaetigung')
                    : $t('delete_gesuch_confirm_title')
            "
            :text="
                toDelete && toDelete.status === GesuchStatus.NEW
                    ? $t('really_delete_rec')
                    : $t('delete_gesuch_confirm_txt')
            "
            @yes="confirmDestroy"
        />
        <ConfirmDlg
            v-model:model-value="extendCommitmentDlg"
            max-width="500"
            :title="$t('extend_commitment')"
            :text="
                $t('really_extend_commitment', {
                    date: extendCommitmentItem?.gueltigkeit_zusage?.format('DD.MM.YYYY'),
                    days: extendCommitmentItem?.gueltigkeit_zusage_in_tagen,
                    daytext:
                        Math.abs(extendCommitmentItem?.gueltigkeit_zusage_in_tagen || 0) === 1
                            ? $t('day')
                            : $t('days'),
                })
            "
            @yes="confirmExtendCommitment"
        />

        <ConfirmDlg
            v-model:model-value="extendRequestedDlg"
            max-width="500"
            :title="$t('extend_requested')"
            :text="$t('extend_msg')"
            :yes-text="$t('close')"
            :show-no-button="false"
        />
    </div>
</template>

<style lang="scss" scoped>
/* .row-pointer >>> tbody tr :hover {
  cursor: pointer;
} */

.gesuch-title {
    font-weight: bold;
}

.gesuch-subtitle {
    font-style: italic;
}

.gesuch-status {
    white-space: nowrap;
    font-weight: normal;
}

.gesuche-liste {
    :deep(tbody tr:hover) {
        background-color: #f7f7f7;
    }
}
</style>
